/**
 * usePinnedReorder
 * -----------------
 * All reorder UX for the pinned section in one place:
 *   • mouse drag (Sortable.js onChoose / onUnchoose / onUpdate plumbing)
 *   • keyboard drag (Tab into the drag handle → Space/Enter grab → ↑/↓ move →
 *     Space/Enter or Esc release)
 *   • right-click "Move" drag (synthesised Sortable.js fallback drag that
 *     behaves like a held-button drag without actually holding the button)
 *
 * Why a composable: each path needs to coordinate with the others
 * (announcing reorders for screen readers, suppressing hover popups while a
 * mouse drag is active, releasing a keyboard grab when contextmenu drag
 * takes over, etc.). Keeping them in side-menu.vue's setup left the
 * component bloated with low-level focus / dispatchEvent / Sortable.js
 * timing logic that wasn't really about the menu's high-level concerns.
 *
 * Depends only on the pinned state machine exposed by usePinnedMenus and on
 * a callback to close hover popups (provided by the host component).
 */
import { ref, onBeforeUnmount, nextTick } from 'vue'
import { announce } from '@zoom/zoom-ui-vue3'
import i18n from '../../../../shared/i18n'

const { t } = i18n.global

const DRAG_HANDLE_SELECTOR = '.side-menu-v3__item-handle'
// Class toggled on <body> while a right-click "Move item" drag is in flight.
// Paired with CSS in side-menu.vue that disables pointer events on the main
// menu list and hover popups so the floating ghost can move freely without
// triggering hover/click on whatever it passes over. See the CSS rule for
// the full rationale.
const CONTEXT_DRAG_BODY_CLASS = 'side-menu-v3-context-dragging'

export function usePinnedReorder({
  pinnedMenus,
  handleSortableReorder,
  handlePinnedHandleKeydown,
  isItemGrabbed,
  releasePinnedGrab,
  closeAllHoverPopups
}) {
  // Shared with the host's template to suppress hover popups & disable the
  // drag-handle's hover affordance while either mouse drag (Sortable.js) or
  // right-click drag is in flight.
  const isPinnedSorting = ref(false)

  // Cleanup handle for an in-flight right-click drag. Held here so a second
  // "Move" call (or component unmount) can tear down lingering document
  // listeners deterministically.
  const contextDragCleanup = ref(null)

  // ---------------- mouse drag (Sortable.js) ----------------

  const handlePinnedSortStart = () => {
    isPinnedSorting.value = true
    closeAllHoverPopups?.()
  }

  const handlePinnedSortEnd = () => {
    isPinnedSorting.value = false
  }

  const handlePinnedSortableReorder = ({ fromIndex, toIndex }) => {
    const result = handleSortableReorder({ fromIndex, toIndex })
    // Mirror the keyboard path's SR feedback so low-vision users dragging
    // with a mouse get the same confirmation.
    if (result?.moved) {
      const moved = pinnedMenus.value[result.newIndex]
      const label = moved?.label ?? t('item')
      announce(t('sideMenu.movedToPosition', [label, result.newIndex + 1, result.total]))
    }
    return result
  }

  // ---------------- keyboard drag (drag handle) ----------------

  const handlePinnedHandleKeyboard = async ({ originalEvent, processedItem }) => {
    const handleEl = originalEvent.currentTarget
    const result = handlePinnedHandleKeydown({
      event: originalEvent,
      menuItem: processedItem.item
    })
    if (!result) return // event not consumed; let outer handlers run

    const label = processedItem.item?.label ?? t('item')

    if (result.type === 'grab') {
      announce(
        t(result.state === 'grabbed' ? 'sideMenu.grabbedKeyboard' : 'sideMenu.dropped', [label])
      )
      return
    }

    if (result.type === 'release-on-escape') {
      announce(t('sideMenu.releasedUnchanged', [label]))
      return
    }

    if (result.type === 'reorder') {
      if (result.moved) {
        const key = originalEvent.code === 'ArrowUp' ? 'sideMenu.movedUp' : 'sideMenu.movedDown'
        announce(t(key, [label, result.newIndex + 1, result.total]))
        // DOM reorders the v-for entries; the same handle DOM node is reused
        // but the browser may drop focus while the move animates. Re-focusing
        // keeps the grabbed handle in keyboard control.
        await nextTick()
        handleEl?.focus?.()
      } else {
        announce(t('sideMenu.cannotMoveFurther', [label]))
      }
    }
  }

  // Auto-release the keyboard grab when focus truly leaves the pinned
  // section. We can NOT release synchronously: while a keyboard reorder is
  // in flight, Vue's DOM patch removes & re-inserts the focused handle,
  // which fires a transient `focusout` (relatedTarget = null) before our
  // `nextTick` re-focus runs. Releasing then would silently drop the grab
  // so the next ArrowUp/Down on the same handle becomes a no-op (the user
  // perceives "the second arrow failed"). Defer one task and check whether
  // focus actually landed outside any handle before releasing.
  const handlePinnedHandleFocusout = ({ originalEvent }) => {
    const next = originalEvent.relatedTarget
    if (next?.closest?.(DRAG_HANDLE_SELECTOR)) return
    setTimeout(() => {
      const ae = document.activeElement
      if (ae?.closest?.(DRAG_HANDLE_SELECTOR)) return
      releasePinnedGrab()
    }, 0)
  }

  // ---------------- right-click drag (contextmenu "Move item") ----------------

  // Behaves exactly like a regular mouse drag *with the mouse button already
  // held down*: ghost, placeholder slot and FLIP animation all come from
  // Sortable.js, model mutations go through the same onUpdate path as a
  // real drag.
  //
  // Implementation: synthesise the start of a fallback Sortable drag
  // (pointerdown on the row to arm `_onTapStart`, then a pointermove past
  // the touchStartThreshold to trigger `_onDragStart` and append the
  // ghost), then let the real cursor's pointermove events drive Sortable.
  // Drop the drag on the next user mousedown by dispatching a synthetic
  // pointerup (Sortable's drop listener with `supportPointer: true`);
  // Escape cancels by dropping and reversing.
  //
  // Sortable's pinned instance is configured with `supportPointer: true`,
  // so the move/up events must be PointerEvents — MouseEvents are filtered
  // out by Sortable.js in pointer mode.
  const firePointer = (type, x, y, target) => {
    ;(target || document).dispatchEvent(
      new PointerEvent(type, {
        bubbles: true,
        cancelable: true,
        view: window,
        pointerType: 'mouse',
        isPrimary: true,
        button: 0,
        buttons: type === 'pointerup' ? 0 : 1,
        clientX: x,
        clientY: y,
        screenX: x,
        screenY: y
      })
    )
  }

  const handlePinnedContextMove = ({ processedItem, rowEl }) => {
    if (!processedItem) return
    const menuItem = processedItem.item
    if (!menuItem || !rowEl) return

    // Re-entrancy: a second "Move" while one is in flight cancels the
    // previous session so listeners don't stack up.
    if (contextDragCleanup.value) contextDragCleanup.value({ silent: true })

    // Keyboard grab and contextmenu drag are mutually exclusive UIs over
    // the same model — drop any lingering keyboard grab.
    if (isItemGrabbed(menuItem)) releasePinnedGrab()

    const initialIndex = pinnedMenus.value.findIndex((m) => m.key === menuItem.key)
    if (initialIndex === -1) return

    const label = menuItem.label ?? t('item')
    const rect = rowEl.getBoundingClientRect()
    const startX = rect.left + rect.width / 2
    const startY = rect.top + rect.height / 2

    // Cache cursor for the synthetic drop / cancel pointerup so Sortable
    // lands the ghost where the user actually sees it.
    let lastX = startX
    let lastY = startY

    // Match the mouse-drag entry: close any open hover popup so the ghost
    // doesn't fly over a stale popup that the user can no longer dismiss
    // (we'll also disable pointer events on `.side-menu-v3__hover-popup`
    // below, but closing it first avoids a brief visual flicker).
    closeAllHoverPopups?.()

    // 1. pointerdown on the row arms Sortable (`_onTapStart` → registers
    //    pointermove on document, marks the row chosen).
    firePointer('pointerdown', startX, startY, rowEl)
    // 2. pointermove past `touchStartThreshold` on the next tick so
    //    Sortable's deferred startup has time to register its document
    //    listener. Fires `_onTouchMove` → `_onDragStart` which clones the
    //    row into a fixed-positioned ghost on <body>, inserts a placeholder
    //    slot, and starts the FLIP animation loop.
    queueMicrotask(() => {
      firePointer('pointermove', startX, startY + 8)
      // Apply *after* Sortable's _onDragStart has hit-tested the pinned
      // row — applying earlier would risk Sortable's pointermove finding
      // nothing under the cursor on browsers that re-hit-test on every
      // event. From here on the main menu & popups are inert, so passing
      // the ghost over them no longer fires hover/click handlers.
      document.body.classList.add(CONTEXT_DRAG_BODY_CLASS)
    })

    announce(t('sideMenu.grabbedMouse', [label]))

    const trackUserMouse = (e) => {
      lastX = e.clientX
      lastY = e.clientY
    }

    let onUserMouseDown
    let onKeydown

    const cleanup = ({ silent = false } = {}) => {
      document.removeEventListener('mousemove', trackUserMouse)
      document.removeEventListener('mousedown', onUserMouseDown, true)
      document.removeEventListener('keydown', onKeydown, true)
      document.body.classList.remove(CONTEXT_DRAG_BODY_CLASS)
      contextDragCleanup.value = null
      if (silent) return
    }

    // Drop on the next user mousedown (capture so we win over child
    // handlers). Sortable runs onEnd → onUpdate synchronously inside the
    // pointerup dispatch, so by the time it returns pinnedMenus has
    // settled.
    onUserMouseDown = (e) => {
      // Eat the upcoming click so dropping on top of an <a href> doesn't
      // navigate. Belt-and-braces timeout removal in case the click never
      // arrives (synthetic mousedown in tests, OS interruption, ...).
      const swallowClick = (ce) => {
        ce.preventDefault()
        ce.stopPropagation()
        document.removeEventListener('click', swallowClick, true)
      }
      document.addEventListener('click', swallowClick, true)
      setTimeout(() => document.removeEventListener('click', swallowClick, true), 0)

      firePointer('pointerup', e.clientX, e.clientY)
      cleanup()

      const finalIndex = pinnedMenus.value.findIndex((m) => m.key === menuItem.key)
      if (finalIndex !== -1 && finalIndex !== initialIndex) {
        announce(`${label} dropped at position ${finalIndex + 1} of ${pinnedMenus.value.length}.`)
      } else {
        announce(`${label} dropped, position unchanged.`)
      }
      e.stopPropagation()
    }

    onKeydown = (e) => {
      if (e.code !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      // Let Sortable finalise at the current cursor first so its visuals
      // tear down cleanly (ghost removed, placeholder dropped, animations
      // flushed), then reverse the move with a single reorder call (the
      // contextmenu drag, like a mouse drag, only shifts one item).
      firePointer('pointerup', lastX, lastY)
      cleanup()
      queueMicrotask(() => {
        const finalIndex = pinnedMenus.value.findIndex((m) => m.key === menuItem.key)
        if (finalIndex !== -1 && finalIndex !== initialIndex) {
          handleSortableReorder({ fromIndex: finalIndex, toIndex: initialIndex })
        }
        announce(`${label} reorder cancelled.`)
      })
    }

    document.addEventListener('mousemove', trackUserMouse)
    document.addEventListener('mousedown', onUserMouseDown, true)
    document.addEventListener('keydown', onKeydown, true)

    contextDragCleanup.value = cleanup
  }

  // Tear down a potentially in-flight contextmenu drag so its document-level
  // listeners don't leak past the host's lifetime.
  onBeforeUnmount(() => {
    if (contextDragCleanup.value) contextDragCleanup.value({ silent: true })
  })

  return {
    isPinnedSorting,
    handlePinnedSortStart,
    handlePinnedSortEnd,
    handlePinnedSortableReorder,
    handlePinnedHandleKeyboard,
    handlePinnedHandleFocusout,
    handlePinnedContextMove
  }
}
